import { END_OPTIONS, START_OPTIONS, nextRange } from "@/helpers/availability";
import { TimeRange } from "@/interfaces/Availability";
import { faPlus, faXmark } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { FC } from "react";
import { Form } from "react-bootstrap";

interface IProps {
  ranges: TimeRange[];
  onChange: (ranges: TimeRange[]) => void;
  /** Préfixe des erreurs : `weekly.0.ranges`, `exceptions.2.ranges`. */
  errorKey: string;
  errors: Record<string, string>;
  /** Complète les noms accessibles : « du lundi », « du jeu. 24 sept. ». */
  context: string;
  /** Texte d'une journée sans plage. */
  emptyLabel?: string;
  /** Le bouton « + » sur une journée vide : inutile quand un interrupteur l'ouvre déjà. */
  addWhenEmpty?: boolean;
  readOnly?: boolean;
}

/** Plages d'une journée, en ligne : « 09:00 – 12:00 ×  14:00 – 18:00 ×  + ». */
const RangeListEditor: FC<IProps> = ({
  ranges,
  onChange,
  errorKey,
  errors,
  context,
  emptyLabel = "Fermé",
  addWhenEmpty = true,
  readOnly = false,
}) => {
  if (readOnly) {
    return (
      <span className="fx-ranges__readonly">
        {ranges.length
          ? ranges.map((r) => `${r.start}–${r.end}`).join(" · ")
          : emptyLabel}
      </span>
    );
  }

  const added = nextRange(ranges);
  const update = (index: number, patch: Partial<TimeRange>) =>
    onChange(
      ranges.map((range, i) => (i === index ? { ...range, ...patch } : range)),
    );
  // Un même message ne s'affiche qu'une fois, même s'il touche deux plages.
  const messages = Array.from(
    new Set(
      [
        ...ranges.map((_, index) => errors[`${errorKey}.${index}`]),
        errors[errorKey],
      ].filter(Boolean),
    ),
  );

  return (
    <div>
      <div className="fx-ranges">
        <div className="fx-ranges__list">
          {ranges.length === 0 && (
            <span className="fx-day__state">{emptyLabel}</span>
          )}
          {ranges.map((range, index) => {
            const n = index + 1;
            const invalid = !!errors[`${errorKey}.${index}`];
            return (
              <span key={index} className="fx-range">
                <Form.Select
                  size="sm"
                  aria-label={`Début de la plage ${n} ${context}`}
                  value={range.start}
                  isInvalid={invalid}
                  onChange={(event) =>
                    update(index, { start: event.target.value })
                  }
                >
                  {START_OPTIONS.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </Form.Select>
                <span className="fx-range__sep" aria-hidden>
                  –
                </span>
                <Form.Select
                  size="sm"
                  aria-label={`Fin de la plage ${n} ${context}`}
                  value={range.end}
                  isInvalid={invalid}
                  onChange={(event) =>
                    update(index, { end: event.target.value })
                  }
                >
                  {END_OPTIONS.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </Form.Select>
                <button
                  type="button"
                  className="fx-icon-btn"
                  aria-label={`Supprimer la plage ${n} ${context}`}
                  title="Supprimer cette plage"
                  onClick={() => onChange(ranges.filter((_, i) => i !== index))}
                >
                  <FontAwesomeIcon icon={faXmark} />
                </button>
              </span>
            );
          })}
        </div>
        {(ranges.length > 0 || addWhenEmpty) && (
          <button
            type="button"
            className="fx-icon-btn fx-icon-btn--add"
            aria-label={`Ajouter une plage ${context}`}
            title={
              added
                ? "Ajouter une plage (par exemple après une pause)"
                : "Journée complète : plus de plage possible"
            }
            disabled={!added}
            onClick={() => added && onChange([...ranges, added])}
          >
            <FontAwesomeIcon icon={faPlus} />
          </button>
        )}
      </div>
      {messages.map((message, index) => (
        <p key={index} className="fx-availability__error" role="alert">
          {message}
        </p>
      ))}
    </div>
  );
};

export default RangeListEditor;
